// Shared helpers for naming the records an issue is about. The #1 UX
// failure was a queue full of "Are these 2 contacts the same person?"
// with no name — a steward couldn't tell WHO was a possible dup. The
// Claude design's queue leads with the record names ("Joseph · Steven
// Lazorchak"); the decision question is the secondary basis line. These
// helpers derive that named title from the issue's affectedRecords.

import { normalizeDisplayName } from "../../../shared/lib/normalize-display-name";
import type { CleanupIssueSummary } from "../api";
import {
  householdClusterRefs as householdClusterRefsPure,
  isImportStubWithCandidate as isImportStubWithCandidatePure,
  loanHouseholdMergeAlternative as loanHouseholdMergeAlternativePure,
  type LoanMergeAlternative,
  isUnconfirmedSplitFamily as isUnconfirmedSplitFamilyPure,
  type RecordRef,
} from "./household-cluster-predicate";
import { householdSearchMergeSource as householdSearchMergeSourcePure } from "./household-search-merge-predicate";
import {
  stubTwinChoices as stubTwinChoicesPure,
  type StubTwinChoices,
} from "./stub-twin-predicate";
import { contactMoveSource as contactMoveSourcePure } from "./contact-move-predicate";
import {
  recordMoveTarget as recordMoveTargetPure,
  type RecordMoveTarget,
} from "./record-move-predicate";
import { realtyLinkSource as realtyLinkSourcePure } from "./realty-link-predicate";

export type { RecordRef };
export type { RecordMoveTarget };

// Grant 2026-05-16 doctrine: "All caps is awful data consistency.
// Things need to be put in place so they do not show up as all caps."
// The shared normalizer is idempotent + conservative. Every cleanup
// display surface routes record names through this so an AC-imported
// "PETA SIMPSON" renders "Peta Simpson" (the stored value is
// untouched; the D1.1 capitalization detector is the data-level fix).
export function displayLabel(label: string): string {
  return normalizeDisplayName(label) || label;
}

const KIND_NOUN: Record<string, string> = {
  contact: "contact",
  household: "household",
  transaction: "transaction",
  opportunity: "opportunity",
  service_record: "service record",
  policy: "policy",
  vendor: "vendor",
  business_entity: "employer",
  activity: "activity",
  rhythm_instance: "care plan",
};

function kindNoun(kind: string, plural: boolean): string {
  const n = KIND_NOUN[kind] ?? kind;
  return plural ? `${n}s` : n;
}

// The bold title for a queue row / research header: the actual record
// name(s). Duplicate-detector clusters often legitimately carry the
// SAME label on both sides (that's WHY they're flagged) — in that case
// show the name once + a "×N" count rather than "Peta Simpson · Peta
// Simpson". Distinct labels are joined with " · " like the design.
export function recordTitle(issue: CleanupIssueSummary): string {
  const recs = issue.affectedRecords ?? [];
  const labels = recs
    .map((r) => displayLabel((r.label ?? "").trim()))
    .filter(Boolean);
  if (labels.length === 0) {
    // No labels resolved — fall back to the question so the row is
    // never blank (still better with the kind prefix the row adds).
    return issue.decisionQuestion;
  }
  const unique = Array.from(new Set(labels));
  if (unique.length === 1) {
    const only = unique[0]!;
    return recs.length > 1
      ? `${only} — ${recs.length} ${kindNoun(issue.entityKind, true)}`
      : only;
  }
  return unique.join(" · ");
}

// "Which Jones?" (Ryan, 2026-07-02) — the distinguishing people line
// for household rows: distinct members_label values across the issue's
// household refs, joined like the title. Null when the server attached
// none (non-household issues, or households with no named members).
export function recordMembersLine(issue: CleanupIssueSummary): string | null {
  const labels = (issue.affectedRecords ?? [])
    .filter((r) => r.kind === "household")
    .map((r) => (r.members_label ?? "").trim())
    .filter(Boolean);
  if (labels.length === 0) return null;
  return Array.from(new Set(labels)).join(" · ");
}

// The muted second line: what we're actually deciding. Short by
// construction (detectors phrase decisionQuestion as a one-liner).
export function recordBasis(issue: CleanupIssueSummary): string {
  return issue.decisionQuestion;
}

// The two records to compare side-by-side, when this issue is a
// 2-record same-kind cluster (the dominant dedup shape). Returns null
// for clusters/structure issues that don't have a clean A-vs-B compare.
export function comparablePair(
  issue: CleanupIssueSummary,
): { a: RecordRef; b: RecordRef } | null {
  const recs = issue.affectedRecords ?? [];
  if (
    recs.length === 2 &&
    recs[0] &&
    recs[1] &&
    recs[0].kind === recs[1].kind
  ) {
    return { a: recs[0], b: recs[1] };
  }
  return null;
}

export function kindLabel(kind: string): string {
  return kindNoun(kind, false);
}

// Every contact in the issue, when it's a 2+-contact duplicate
// cluster. Drives the full per-field cluster merge (the orchestrator
// handles N=2 inline AND N≥3 pairwise + cluster-wide undo) so a
// 3-record "Patricia Comer-Lopez" cluster is no longer a dead end.
export function contactClusterRefs(
  issue: CleanupIssueSummary,
): RecordRef[] | null {
  const recs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "contact",
  );
  return recs.length >= 2 ? recs : null;
}

// Baker 2026-05-22: the same shape, for HOUSEHOLD-level merge issues.
// The mortgage stub-household-merge detector emits a 2-record household
// cluster (stub + candidate). The quick-merge path picks recs[0] as
// survivor blindly — which for the stub detector is the STUB (wrong),
// AND skips per-field review. Returning the refs here lets DecideRail
// render the "Review every field, then merge…" button so the user
// picks the survivor explicitly and reviews each field, just like the
// contact path. Restricted to merge-recommended issues so we don't
// surface the button on every household issue.
//
// Predicate lives in ./household-cluster-predicate (pure, root-safe,
// unit-tested in isolation); this wrapper preserves the
// CleanupIssueSummary call signature DecideRail uses.
export function householdClusterRefs(
  issue: CleanupIssueSummary,
): RecordRef[] | null {
  return householdClusterRefsPure(issue);
}

// Grant 2026-07-25 — true when this is the household-address-split-family
// "different surname" branch (no auto-recommendation — the human must
// confirm it's one family first). DecideRail uses this to swap the
// "recommended survivor pre-selected" copy for honest "pick who survives"
// copy, since there's no recommendation to pre-select from.
export function isUnconfirmedSplitFamily(issue: CleanupIssueSummary): boolean {
  return isUnconfirmedSplitFamilyPure(issue);
}

// Grant 2026-09-15 — true when this is an import-stub review card that
// NAMES the family its record points at (two household refs, and no
// recommendation, because the data does not settle it). DecideRail swaps
// the generic merge button for the three answers those cards need.
// Predicate lives in ./household-cluster-predicate.
export function isImportStubWithCandidate(issue: CleanupIssueSummary): boolean {
  return isImportStubWithCandidatePure(issue);
}

// Grant 2026-09-16 — the second answer on a "should this loan move?"
// card: the two households are one person written twice, so merge them
// rather than moving the record onto the duplicate. Predicate lives in
// ./household-cluster-predicate.
export function loanHouseholdMergeAlternative(
  issue: CleanupIssueSummary,
): LoanMergeAlternative | null {
  return loanHouseholdMergeAlternativePure(issue);
}

// Grant 2026-07-08: spouse-pass / trust-reattach's review branch only
// know ONE household (the source) — the destination has to be found by
// search, unlike the 2-known-households cluster case above. Predicate
// lives in ./household-search-merge-predicate (pure, root-safe,
// unit-tested in isolation); this wrapper preserves the
// CleanupIssueSummary call signature DecideRail uses.
export function householdSearchMergeSource(
  issue: CleanupIssueSummary,
): RecordRef | null {
  return householdSearchMergeSourcePure(issue);
}

// Cambria 2026-07-31: the mortgage-stub twin picker — one placeholder
// household facing N identically-named real families, where the steward
// has to say WHICH family it belongs to. Distinct from the two shapes
// above: the cluster path needs exactly two known households, and the
// search path needs zero known destinations. Predicate lives in
// ./stub-twin-predicate (pure, root-safe, unit-tested in isolation).
export function stubTwinChoices(
  issue: CleanupIssueSummary,
): StubTwinChoices | null {
  return stubTwinChoicesPure(issue);
}

// Grant 2026-07-08: contact-no-household's `move` recommendation had no
// decide-rail handling at all. Predicate lives in
// ./contact-move-predicate (pure, root-safe, unit-tested in isolation).
export function contactMoveSource(
  issue: CleanupIssueSummary,
): RecordRef | null {
  return contactMoveSourcePure(issue);
}

// 2026-09-12: the RECORD counterpart, and the same bug one door over —
// `move-household` is recommended by three detectors and has no decide
// case, so the recommended button came back 501 `verb_not_wired`.
// Predicate lives in ./record-move-predicate (pure, root-safe, unit-tested
// in isolation).
export function recordMoveTarget(
  issue: CleanupIssueSummary,
): RecordMoveTarget | null {
  return recordMoveTargetPure(issue);
}

// Nate 2026-07-09: the realty-sr-needs-client-household issue needs a
// household PICKER (it links a service_record to a client household — a
// different backend action than a household merge). Predicate lives in
// ./realty-link-predicate (pure, root-safe, unit-tested in isolation).
export function realtyLinkSource(
  issue: CleanupIssueSummary,
): RecordRef | null {
  return realtyLinkSourcePure(issue);
}

// detectorSlug → the DuplicateCluster match_type the orchestrator
// expects. Anything not email/phone keys as name_zip (the generic
// "same person, multiple rows" shape).
export function duplicateMatchType(
  detectorSlug: string,
): "email" | "phone" | "name_zip" {
  if (detectorSlug === "dup-email") return "email";
  if (detectorSlug === "dup-phone") return "phone";
  return "name_zip";
}

// Friction-tier badge → the design's tinted-chip class + label.
// "confirm" is Stewardship yellow (.cu-tier--confirm), never the
// golden --warning (Grant 2026-05-18). Render as:
//   <span className={tierChip(t).className}>{tierChip(t).label}</span>
export function tierChip(tier: string): {
  label: string;
  className: string;
} {
  switch (tier) {
    case "auto":
      return { label: "Auto", className: "cu-tier cu-tier--auto" };
    case "confirm":
      return { label: "Confirm", className: "cu-tier cu-tier--confirm" };
    case "restricted":
      return {
        label: "Restricted",
        className: "cu-tier cu-tier--restricted",
      };
    default:
      return {
        label: "Standard",
        className: "cu-tier cu-tier--standard",
      };
  }
}
